import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import type { Session } from "@supabase/supabase-js";
import { ArrowLeft } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/utils/supabase";
import { PublicNavbar } from "@/components/public-navbar";
import { SeoHead } from "@/components/seo-head";

interface SectionMeta {
  id: string;
  title: string;
  group: "overview" | "ui" | "mcp";
  description: string;
  jsonLd?: object;
}

const QUICKSTART_HOW_TO = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to connect an LLM agent to TodoLLM via MCP",
  description:
    "Set up TodoLLM and wire an LLM agent to it using the Model Context Protocol (MCP) so the agent can autonomously claim and execute tasks.",
  totalTime: "PT10M",
  step: [
    {
      "@type": "HowToStep",
      position: 1,
      name: "Sign in and create an account",
      text: "Create an account or sign in at /auth. Magic-link email - no password required. A Personal organization is created automatically.",
      url: "https://todollm.com/docs/quickstart",
    },
    {
      "@type": "HowToStep",
      position: 2,
      name: "Create a project",
      text: "From the dashboard click New project. Give it a name and a URL slug (e.g. my-app). Projects are the containers that hold tasks.",
      url: "https://todollm.com/docs/quickstart",
    },
    {
      "@type": "HowToStep",
      position: 3,
      name: "Generate a scoped API key",
      text: "Go to Settings → API keys and mint a key with the tasks:read, tasks:write, and tasks:operate scopes. Copy the secret - it is shown once.",
      url: "https://todollm.com/docs/api-keys",
    },
    {
      "@type": "HowToStep",
      position: 4,
      name: "Configure the MCP URL in your agent client",
      text: "Add the TodoLLM MCP server to your agent's mcpServers config, pointing at https://todollm.com/mcp with Authorization: Bearer <your-key> in the headers.",
      url: "https://todollm.com/docs/mcp-setup",
    },
    {
      "@type": "HowToStep",
      position: 5,
      name: "Call list_tasks to discover work",
      text: "The agent calls list_tasks(status='PENDING', label='TODO') to find unclaimed tasks ready for execution.",
      url: "https://todollm.com/docs/mcp-tools",
    },
    {
      "@type": "HowToStep",
      position: 6,
      name: "Call claim_task to lock the task",
      text: "The agent calls claim_task(task_id, note) to set the task status to IN_PROGRESS and record its identity as the claimant.",
      url: "https://todollm.com/docs/mcp-tools",
    },
    {
      "@type": "HowToStep",
      position: 7,
      name: "Call update_task to complete or fail the task",
      text: "After doing the work, the agent calls update_task(task_id, status='COMPLETED', note, commit_hash) to close the task with an audit trail entry.",
      url: "https://todollm.com/docs/mcp-tools",
    },
  ],
};

export const SECTIONS: SectionMeta[] = [
  {
    id: "overview",
    title: "Overview",
    group: "overview",
    description:
      "What TodoLLM is - a centralized task-state engine for hybrid human+agent teams with an append-only timeline.",
  },
  {
    id: "quickstart",
    title: "Quickstart",
    group: "overview",
    description:
      "Get up and running with TodoLLM in five steps: sign in, create a project, add tasks, connect an agent, and watch it work.",
    jsonLd: QUICKSTART_HOW_TO,
  },
  {
    id: "accounts",
    title: "Accounts & sign-in",
    group: "ui",
    description:
      "How authentication works in TodoLLM: email + password via Supabase Auth, account creation, and personal organization setup.",
  },
  {
    id: "organizations",
    title: "Organizations",
    group: "ui",
    description:
      "Organizations are the top-level multi-tenant boundary. Learn about roles (owner, admin, member) and how to invite collaborators.",
  },
  {
    id: "projects",
    title: "Projects",
    group: "ui",
    description:
      "Projects group tasks within an organization. Each has a unique slug and can be scoped with an API key for agent access.",
  },
  {
    id: "tasks",
    title: "Tasks: labels & status",
    group: "ui",
    description:
      "Every task has a label (IDEA, TODO, TRIAGE) and a status (PENDING, IN_PROGRESS, COMPLETED, FAILED, DROPPED). Learn the lifecycle.",
  },
  {
    id: "timeline",
    title: "Timeline",
    group: "ui",
    description:
      "An append-only audit trail attached to every task. Immutable rows record actor, status transition, commit hash, and a 200-char note.",
  },
  {
    id: "api-keys",
    title: "API keys",
    group: "ui",
    description:
      "Mint scoped API keys (tasks:read, tasks:write, tasks:operate) from Settings. Keys are argon2-hashed and shown once at creation.",
  },
  {
    id: "mcp-overview",
    title: "MCP server: what it is",
    group: "mcp",
    description:
      "TodoLLM's remote MCP endpoint lets LLM agents connect via Streamable HTTP transport - no per-machine install required.",
  },
  {
    id: "mcp-setup",
    title: "Wiring it into a client",
    group: "mcp",
    description:
      "Step-by-step guide for connecting Claude Desktop, Claude Code, or any MCP-compatible client to the TodoLLM MCP server.",
  },
  {
    id: "mcp-clients",
    title: "Quick setup by client",
    group: "mcp",
    description:
      "One-command or one-file setup for Claude Code, Cursor, Windsurf, and VS Code Copilot - all project-scoped so each API key stays bound to its project.",
  },
  {
    id: "mcp-dot-mcp",
    title: ".mcp.json example",
    group: "mcp",
    description:
      "Repo-local MCP configuration via .mcp.json - keep credentials out of version control while wiring TodoLLM to your codebase.",
  },
  {
    id: "mcp-tools",
    title: "Tool reference",
    group: "mcp",
    description:
      "Full reference for all MCP tools: list_tasks, get_history, claim_task, update_task, and add_task — including dependency management, reordering, and blocked-task filtering.",
  },
  {
    id: "mcp-loop",
    title: "Agentic execution loop",
    group: "mcp",
    description:
      "The recommended five-step agent loop: query, claim, context, action, close - with TodoLLM tool calls at each step.",
  },
  {
    id: "mcp-prompts",
    title: "Prompts & agent workflows",
    group: "mcp",
    description:
      "Four built-in MCP prompts that teach any agent how to claim, complete, triage, and review tasks — plus a sample system prompt for quick bootstrapping.",
  },
];

export function DocsPage() {
  const { section } = useParams({ strict: false }) as { section?: string };
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) =>
      setSession(s),
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  const meta = SECTIONS.find((s) => s.id === section) ?? SECTIONS[0];
  const Content = SECTION_COMPONENTS[meta.id] ?? OverviewContent;

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://todollm.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Documentation",
        item: "https://todollm.com/docs/overview",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: meta.title,
        item: `https://todollm.com/docs/${meta.id}`,
      },
    ],
  };

  const jsonLd: object[] = [breadcrumb, ...(meta.jsonLd ? [meta.jsonLd] : [])];

  return (
    <div className="min-h-screen">
      <SeoHead
        title={`${meta.title} - Documentation`}
        description={meta.description}
        canonical={`https://todollm.com/docs/${meta.id}`}
        jsonLd={jsonLd}
      />
      <PublicNavbar />

      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-10 lg:grid-cols-[16rem_1fr]">
        <aside className="hidden lg:block">
          <nav className="sticky top-24 space-y-6 text-sm">
            <NavGroup label="Overview" group="overview" active={meta.id} />
            <NavGroup label="Client UI" group="ui" active={meta.id} />
            <NavGroup label="MCP server" group="mcp" active={meta.id} />
          </nav>
        </aside>

        <article className="prose-docs space-y-12">
          {/* Mobile section picker */}
          <div className="lg:hidden">
            <select
              value={meta.id}
              onChange={(e) =>
                navigate({
                  to: "/docs/$section",
                  params: { section: e.target.value },
                })
              }
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            >
              {(
                [
                  { key: "overview", label: "Overview" },
                  { key: "ui", label: "Client UI" },
                  { key: "mcp", label: "MCP server" },
                ] as { key: SectionMeta["group"]; label: string }[]
              ).map(({ key, label }) => (
                <optgroup key={key} label={label}>
                  {SECTIONS.filter((s) => s.group === key).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <Content />
          <FooterCta session={session} />
        </article>
      </div>
    </div>
  );
}

function NavGroup({
  label,
  group,
  active,
}: {
  label: string;
  group: SectionMeta["group"];
  active: string;
}) {
  const items = SECTIONS.filter((s) => s.group === group);
  return (
    <div>
      <p className="mb-1 text-[0.65rem] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <ul className="space-y-0.5">
        {items.map((s) => (
          <li key={s.id}>
            <Link
              to="/docs/$section"
              params={{ section: s.id }}
              className={`block rounded-md px-2 py-1 hover:bg-accent/60 hover:text-foreground ${
                s.id === active
                  ? "bg-accent/60 text-foreground font-medium"
                  : "text-muted-foreground"
              }`}
            >
              {s.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="scroll-mt-24 text-2xl font-semibold tracking-tight">
      {children}
    </h2>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="mt-6 text-lg font-medium">{children}</h3>;
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="leading-7 text-foreground/90">{children}</p>;
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-foreground/90">
      {children}
    </code>
  );
}

function Pre({ children }: { children: React.ReactNode }) {
  return (
    <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-4 text-xs leading-relaxed">
      {children}
    </pre>
  );
}

function SectionWrap({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <H2 id={id}>{title}</H2>
      {children}
    </section>
  );
}

function OverviewContent() {
  return (
    <SectionWrap id="overview" title="Overview">
      <P>
        TodoLLM is a centralized task-state engine for hybrid teams of humans
        and LLM agents. The same task list - with an append-only timeline of
        every claim, status change, and progress note - is read and written from
        both the web dashboard and an MCP server that agents speak.
      </P>
      <P>
        These docs cover the two surfaces: the <strong>web UI</strong> that
        humans use to organize work, and the <strong>MCP server</strong> that
        agents use to autonomously claim and execute it.
      </P>
    </SectionWrap>
  );
}

function QuickstartContent() {
  return (
    <SectionWrap id="quickstart" title="Quickstart">
      <ol className="space-y-3 leading-7 text-foreground/90 [counter-reset:steps]">
        {[
          {
            title: "Sign in",
            body: (
              <>
                Create an account or sign in at <Code>/auth</Code>. Magic-link
                email - no password required.
              </>
            ),
          },
          {
            title: "Create a project",
            body: (
              <>
                From the dashboard click <strong>New project</strong>. Give it a
                name and a URL slug (e.g. <Code>my-app</Code>). Projects are the
                containers that hold tasks.
              </>
            ),
          },
          {
            title: "Add tasks",
            body: (
              <>
                Use the <strong>+ Task</strong> button in the UI, or let your
                agent call the MCP <Code>add_task</Code> tool. Label tasks{" "}
                <Code>TODO</Code>, <Code>IDEA</Code>, or <Code>TRIAGE</Code>.
                Title ≤ 200 chars, description ≤ 400 chars.
              </>
            ),
          },
          {
            title: "Connect your agent",
            body: (
              <>
                Generate an API key under <strong>Settings → API keys</strong>{" "}
                with the <Code>tasks:read tasks:write tasks:operate</Code>{" "}
                scopes, then wire it into your MCP client - see{" "}
                <Link
                  to="/docs/$section"
                  params={{ section: "mcp-setup" }}
                  className="underline underline-offset-2"
                >
                  Wiring it into a client
                </Link>
                .
              </>
            ),
          },
          {
            title: "Let the agent work",
            body: (
              <>
                The agent calls <Code>list_tasks</Code> to find work,{" "}
                <Code>claim_task</Code> to lock a task, and{" "}
                <Code>update_task</Code> to post progress and mark completion.
                Check the <strong>Timeline</strong> tab for a full append-only
                audit trail.
              </>
            ),
          },
        ].map(({ title, body }, i) => (
          <li
            key={i}
            className="grid grid-cols-[2rem_1fr] gap-2 [counter-increment:steps]"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
              {i + 1}
            </span>
            <span>
              <strong>{title}</strong> - {body}
            </span>
          </li>
        ))}
      </ol>
    </SectionWrap>
  );
}

function AccountsContent() {
  return (
    <SectionWrap id="accounts" title="Accounts & sign-in">
      <P>
        Authentication is email + password via Supabase Auth. There is no
        external email - accounts confirm immediately. On first sign-up the
        server creates a profile plus a default <Code>Personal</Code>{" "}
        organization with you as the owner.
      </P>
    </SectionWrap>
  );
}

function OrganizationsContent() {
  return (
    <SectionWrap id="organizations" title="Organizations">
      <P>
        An organization is the top-level multi-tenant boundary. Members hold an{" "}
        <Badge variant="outline">owner</Badge>,{" "}
        <Badge variant="outline">admin</Badge>, or{" "}
        <Badge variant="outline">member</Badge> role. Owners and admins can
        create projects and invite others (invitations land in-app - no email).
      </P>
      <H3>Creating an organization</H3>
      <P>
        From the home page, fill in the "Create organization" form. You become
        its owner. Names are non-unique; identification is by UUID.
      </P>
    </SectionWrap>
  );
}

function ProjectsContent() {
  return (
    <SectionWrap id="projects" title="Projects">
      <P>
        A project belongs to one organization and groups tasks. Each project has
        a <Code>slug</Code> unique within its organization. Projects are created
        in the web UI; the MCP server reads them from the same database and
        accepts API keys scoped to either an organization or a specific project.
      </P>
    </SectionWrap>
  );
}

function TasksContent() {
  return (
    <SectionWrap id="tasks" title="Tasks: labels & status">
      <P>
        Each task carries a <strong>label</strong> describing its kind and a{" "}
        <strong>status</strong> describing where it is in execution.
      </P>
      <H3>Labels</H3>
      <ul className="ml-4 list-disc space-y-1 text-sm">
        <li>
          <Badge variant="outline" className="font-mono">
            IDEA
          </Badge>{" "}
          - a concept or brainstorm not ready for implementation.
        </li>
        <li>
          <Badge variant="outline" className="font-mono">
            TODO
          </Badge>{" "}
          - an actionable implementation item.
        </li>
        <li>
          <Badge variant="outline" className="font-mono">
            TRIAGE
          </Badge>{" "}
          - requires human or agent analysis to categorize.
        </li>
      </ul>
      <H3>Status</H3>
      <ul className="ml-4 list-disc space-y-1 text-sm">
        <li>
          <Badge variant="outline">PENDING</Badge> - not yet picked up.
        </li>
        <li>
          <Badge variant="info">IN_PROGRESS</Badge> - claimed by a user or
          agent. Whoever claims sets <Code>claimed_by</Code>.
        </li>
        <li>
          <Badge variant="success">COMPLETED</Badge> - done. Agents typically
          attach a <Code>commit_hash</Code> here.
        </li>
        <li>
          <Badge variant="destructive">FAILED</Badge> - couldn't finish. Append
          a note explaining why so the next claimant has context.
        </li>
        <li>
          <Badge variant="secondary">DROPPED</Badge> - task was considered but
          intentionally not implemented. Closes the task without success.
        </li>
      </ul>
      <P>
        Status transitions are unrestricted in this MVP: humans can rescue a
        stuck agent task, and agents can correct their own state. Every
        transition writes a timeline row.
      </P>
    </SectionWrap>
  );
}

function TimelineContent() {
  return (
    <SectionWrap id="timeline" title="Timeline">
      <P>
        Every task has a chronological, <strong>append-only</strong> timeline.
        Each row records the actor (user email or <Code>apikey:{`<id>`}</Code>),
        the status before and after, an optional commit hash, and a{" "}
        <strong>200-character note</strong>.
      </P>
      <P>
        Notes cannot be edited or deleted - the database revokes{" "}
        <Code>UPDATE</Code> and <Code>DELETE</Code> on the timeline table.
        Course-correct by appending a new note.
      </P>
      <P>
        For agents, fetch <Code>tail: 1</Code> to see only the last entry, or a
        small <Code>head</Code>/<Code>tail</Code> to keep context windows
        bounded.
      </P>
    </SectionWrap>
  );
}

function ApiKeysContent() {
  return (
    <SectionWrap id="api-keys" title="API keys">
      <P>
        API keys authenticate the MCP server (and any other agent client) to the
        HTTP API. Mint them at <Code>Settings → API keys</Code>.
      </P>
      <H3>Scopes</H3>
      <ul className="ml-4 list-disc space-y-1 text-sm">
        <li>
          <Code>tasks:read</Code> - list tasks and read timelines.
        </li>
        <li>
          <Code>tasks:write</Code> - create new tasks and edit descriptions.
        </li>
        <li>
          <Code>tasks:operate</Code> - change status or claim ownership.
        </li>
      </ul>
      <H3>Storage & lifecycle</H3>
      <P>
        Keys are stored as an <Code>argon2</Code> hash of the secret plus the
        plaintext prefix used for lookup. The full secret is shown{" "}
        <strong>once</strong> at creation; copy it then or revoke and
        regenerate. Revoking sets <Code>revoked_at</Code>; subsequent requests
        with that key are rejected immediately.
      </P>
      <P>
        Scope a key to the smallest org/project + permission set the agent
        needs. A read-only auditing agent should not have{" "}
        <Code>tasks:operate</Code>.
      </P>
    </SectionWrap>
  );
}

function McpOverviewContent() {
  return (
    <SectionWrap id="mcp-overview" title="MCP server: what it is">
      <P>
        TodoLLM exposes a remote{" "}
        <a
          className="underline underline-offset-4"
          href="https://modelcontextprotocol.io"
          target="_blank"
          rel="noreferrer"
        >
          Model Context Protocol
        </a>{" "}
        endpoint at <Code>/mcp</Code> on the same deployment that serves the web
        dashboard. There is nothing to install per-machine - your agent's MCP
        client connects directly over HTTP using the Streamable HTTP transport
        (single endpoint, GET/POST/DELETE).
      </P>
      <P>
        Every request authenticates with an API key minted from{" "}
        <Code>Settings → API keys</Code>, sent as{" "}
        <Code>Authorization: Bearer sk_todollm_…</Code>. Project scoping is
        carried by the key itself: a project-scoped key auto-binds to its
        project, while an org-scoped key requires an explicit{" "}
        <Code>project_id</Code> argument on tools that operate on tasks.
      </P>
    </SectionWrap>
  );
}

function McpSetupContent() {
  return (
    <SectionWrap id="mcp-setup" title="Wiring it into a client">
      <P>
        Mint an API key from <Code>Settings → API keys</Code> - the copy-once
        dialog renders the exact JSON snippet you can drop into your MCP
        client's settings.
      </P>
      <H3>Claude Desktop / Claude Code</H3>
      <P>
        Add this entry under <Code>mcpServers</Code>:
      </P>
      <Pre>{`{
  "mcpServers": {
    "todollm": {
      "url": "https://todollm.example.com/mcp",
      "headers": {
        "Authorization": "Bearer sk_todollm_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"
      }
    }
  }
}`}</Pre>
      <H3>VS Code Copilot Chat</H3>
      <P>
        Create or edit <Code>.vscode/mcp.json</Code> in your workspace root:
      </P>
      <Pre>{`{
  "servers": {
    "todollm": {
      "type": "http",
      "url": "https://todollm.com/mcp",
      "headers": {
        "Authorization": "Bearer \${input:todoApiKey}"
      }
    }
  },
  "inputs": [
    {
      "type": "promptString",
      "id": "todoApiKey",
      "description": "TodoLLM API Key",
      "password": true
    }
  ]
}`}</Pre>
      <P>
        VS Code prompts for the key once on first connect and stores it
        securely. Do <strong>not</strong> use a static{" "}
        <Code>Authorization</Code> header - VS Code's MCP HTTP transport follows
        the OAuth discovery flow and will error if the server doesn't support
        dynamic client registration.
      </P>
      <P>
        Reload VS Code. In Copilot Chat switch to <strong>Agent</strong> mode (
        <Code>@workspace</Code> → agent icon) and the <Code>todollm</Code> tools
        will appear in the tool picker.
      </P>
      <H3>MCP Inspector / custom clients</H3>
      <P>
        Any client that speaks Streamable HTTP transport works. Point it at{" "}
        <Code>/mcp</Code> on your deployment and pass the API key in the{" "}
        <Code>Authorization</Code> header. The server replies with{" "}
        <Code>401 Unauthorized</Code> if the header is missing or the key is
        invalid/revoked, before any MCP state machinery runs.
      </P>
    </SectionWrap>
  );
}

function McpClientsContent() {
  return (
    <SectionWrap id="mcp-clients" title="Quick setup by client">
      <P>
        Each API key is scoped to one project. Use project-level config files
        (not global/user-level settings) so the right key is used per repo.
      </P>

      <H3>Claude Code</H3>
      <P>
        One command - writes to <Code>.mcp.json</Code> at the project root:
      </P>
      <Pre>{`claude mcp add todollm https://todollm.com/mcp \\
  -t http \\
  -H "Authorization: Bearer sk_todollm_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"`}</Pre>
      <P>
        Verify with <Code>claude mcp list</Code>.
      </P>

      <H3>Cursor</H3>
      <P>
        Create <Code>.cursor/mcp.json</Code> in the project root:
      </P>
      <Pre>{`{
  "mcpServers": {
    "todollm": {
      "url": "https://todollm.com/mcp",
      "headers": {
        "Authorization": "Bearer sk_todollm_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"
      }
    }
  }
}`}</Pre>
      <P>
        Cursor picks this up automatically. Add <Code>.cursor/mcp.json</Code> to{" "}
        <Code>.gitignore</Code> - do not commit live keys.
      </P>

      <H3>Windsurf</H3>
      <P>
        Create <Code>.windsurf/mcp_config.json</Code> in the project root:
      </P>
      <Pre>{`{
  "mcpServers": {
    "todollm": {
      "serverUrl": "https://todollm.com/mcp",
      "headers": {
        "Authorization": "Bearer sk_todollm_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"
      }
    }
  }
}`}</Pre>
      <P>
        Windsurf loads project-level config on workspace open. Add{" "}
        <Code>.windsurf/mcp_config.json</Code> to <Code>.gitignore</Code>.
      </P>

      <H3>VS Code Copilot Chat</H3>
      <P>
        Create <Code>.vscode/mcp.json</Code> in the project root - see{" "}
        <Link
          to="/docs/$section"
          params={{ section: "mcp-setup" }}
          className="underline underline-offset-2"
        >
          Wiring it into a client
        </Link>{" "}
        for the full snippet. Use the <Code>{`\${input:todoApiKey}`}</Code>{" "}
        variable so the key is stored in VS Code's secret store, not on disk.
      </P>

      <H3>Gemini CLI</H3>
      <P>
        Create <Code>.gemini/settings.json</Code> in the project root:
      </P>
      <Pre>{`{
  "mcpServers": {
    "todollm": {
      "httpUrl": "https://todollm.com/mcp",
      "headers": {
        "Authorization": "Bearer sk_todollm_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"
      }
    }
  }
}`}</Pre>
      <P>
        Gemini CLI uses <Code>httpUrl</Code> (not <Code>url</Code>) for HTTP
        endpoints. Add <Code>.gemini/settings.json</Code> to{" "}
        <Code>.gitignore</Code>.
      </P>

      <H3>Antigravity</H3>
      <P>
        Create <Code>.gemini/antigravity/mcp_config.json</Code> in the project
        root:
      </P>
      <Pre>{`{
  "mcpServers": {
    "todollm": {
      "serverUrl": "https://todollm.com/mcp",
      "headers": {
        "Authorization": "Bearer sk_todollm_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"
      }
    }
  }
}`}</Pre>
      <P>
        Antigravity uses <Code>serverUrl</Code>. You can also manage servers
        from the Agent session UI via <strong>… → Manage MCP Servers</strong>.
        Add the file to <Code>.gitignore</Code>.
      </P>

      <H3>Codex CLI</H3>
      <P>
        Create <Code>.codex/config.toml</Code> in the project root:
      </P>
      <Pre>{`[mcp_servers.todollm]
url = "https://todollm.com/mcp"

[mcp_servers.todollm.http_headers]
Authorization = "Bearer sk_todollm_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"`}</Pre>
      <P>
        Codex uses TOML. Prefer storing the key in an env var and referencing it
        via <Code>bearer_token_env_var = "TODOLLM_API_KEY"</Code> instead of a
        static header. Add <Code>.codex/config.toml</Code> to{" "}
        <Code>.gitignore</Code>.
      </P>

      <H3>Open Code (opencode)</H3>
      <P>
        Create or edit <Code>opencode.json</Code> in the project root:
      </P>
      <Pre>{`{
  "$schema": "https://opencode.ai/config.json",
  "mcp": {
    "todollm": {
      "type": "remote",
      "url": "https://todollm.com/mcp",
      "enabled": true,
      "headers": {
        "Authorization": "Bearer sk_todollm_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"
      }
    }
  }
}`}</Pre>
      <P>
        The <Code>type: "remote"</Code> field is required for HTTP servers. Add{" "}
        <Code>opencode.json</Code> to <Code>.gitignore</Code>.
      </P>

      <H3>Any client - via .mcp.json</H3>
      <P>
        A root-level <Code>.mcp.json</Code> is the cross-tool standard. Claude
        Code, Cursor, and other MCP-aware editors all read it. See{" "}
        <Link
          to="/docs/$section"
          params={{ section: "mcp-dot-mcp" }}
          className="underline underline-offset-2"
        >
          .mcp.json example
        </Link>{" "}
        for the format.
      </P>
    </SectionWrap>
  );
}

function McpDotMcpContent() {
  return (
    <SectionWrap id="mcp-dot-mcp" title=".mcp.json example">
      <P>
        If you want repo-local MCP wiring, place a root-level{" "}
        <Code>.mcp.json</Code> file beside your source code and keep the real
        credential out of version control.
      </P>
      <Pre>{`{
  "mcpServers": {
    "todollm": {
      "type": "http",
      "url": "https://todollm.example.com/mcp",
      "headers": {
        "Authorization": "Bearer sk_todollm_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"
      }
    }
  }
}`}</Pre>
      <P>
        Commit only redacted examples like this one. Live API keys should stay
        in ignored local files or your MCP client's secret storage.
      </P>
    </SectionWrap>
  );
}

function ToolEntry({
  name,
  scope,
  signature,
  description,
}: {
  name: string;
  scope: string;
  signature: string;
  description: string;
}) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-4">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <code className="font-mono text-sm font-medium text-primary">
          {name}
        </code>
        <Badge variant="outline" className="font-mono">
          {scope}
        </Badge>
      </div>
      <pre className="mb-2 overflow-x-auto text-xs text-muted-foreground">
        {signature}
      </pre>
      <p className="text-sm leading-6 text-foreground/85">{description}</p>
    </div>
  );
}

function McpToolsContent() {
  return (
    <SectionWrap id="mcp-tools" title="Tool reference">
      <P>
        Tools that operate on a project accept an optional{" "}
        <Code>project_id</Code> parameter. With a project-scoped API key it's
        ignored; with an org-scoped key it's required.
      </P>
      <ToolEntry
        name="list_tasks"
        scope="tasks:read"
        signature="list_tasks(status?: string, label?: string, blocked?: boolean, project_id?: string)"
        description="List tasks in the project, optionally filtered by status, label, and/or blocked state. Pass blocked=false, status=PENDING to find claimable tasks. Each task includes position, depends_on (UUIDs), and is_blocked."
      />
      <ToolEntry
        name="get_history"
        scope="tasks:read"
        signature="get_history(task_id: string, head?: int, tail?: int)"
        description="Read the append-only timeline. Pass tail=1 to see the most recent entry only; pass head=5 to read the first five. Pass nothing for the full timeline. head and tail are mutually exclusive."
      />
      <ToolEntry
        name="claim_task"
        scope="tasks:operate"
        signature="claim_task(task_id: string, note: string)"
        description="Set status to IN_PROGRESS and self-claim the task. Fails if the task is blocked by incomplete dependencies. The note (≤200 chars) is appended to the timeline."
      />
      <ToolEntry
        name="update_task"
        scope="tasks:operate"
        signature="update_task(task_id: string, status?: string, note?: string, commit_hash?: string, add_deps?: string[], remove_deps?: string[], position?: int)"
        description="Update a task: change status, add/remove dependencies, or reorder. status and note are optional when only reordering or updating deps. commit_hash is useful for COMPLETED transitions. add_deps/remove_deps are task UUIDs in the same project; circular deps are rejected."
      />
      <ToolEntry
        name="add_task"
        scope="tasks:write"
        signature="add_task(title: string, label: string, description?: string, note?: string, depends_on?: string[], position?: int, project_id?: string)"
        description="Create a new PENDING task. Label must be IDEA, TODO, or TRIAGE. Title ≤ 200 chars, description ≤ 400 chars. depends_on accepts task UUIDs in the same project. Omit position to append at the end."
      />
    </SectionWrap>
  );
}

function McpLoopContent() {
  return (
    <SectionWrap id="mcp-loop" title="Agentic execution loop">
      <P>
        The recommended pattern (PRD §5) is a sequential five-step loop. Pair it
        with whatever orchestration your agent uses; TodoLLM only cares about
        the tool calls.
      </P>
      <ol className="ml-4 list-decimal space-y-2 text-sm">
        <li>
          <strong>Query.</strong> <Code>list_tasks</Code> with{" "}
          <Code>status=PENDING</Code> and a label that fits the agent (typically{" "}
          <Code>TODO</Code>).
        </li>
        <li>
          <strong>Claim.</strong> Pick one and call <Code>claim_task</Code> with
          a one-line plan in the note. The status flips to{" "}
          <Code>IN_PROGRESS</Code> and your identity is recorded.
        </li>
        <li>
          <strong>Context.</strong> <Code>get_history(task_id, tail: 1)</Code>{" "}
          to read the last known state. Useful when a previous attempt failed or
          a human left guidance.
        </li>
        <li>
          <strong>Action.</strong> Do the actual work - edit files, run tests,
          build. TodoLLM stays out of this step.
        </li>
        <li>
          <strong>Close.</strong> <Code>update_task</Code> to{" "}
          <Code>COMPLETED</Code> or <Code>FAILED</Code>. Pass a concise summary
          as the note and, on success, the <Code>commit_hash</Code>.
        </li>
      </ol>
      <P>
        On <Code>FAILED</Code>, leave enough information in the note that the
        next picker (human or agent) can decide whether to retry, escalate, or
        close.
      </P>
    </SectionWrap>
  );
}

const SYSTEM_PROMPT = `You have access to the TodoLLM MCP server. Use it to track your work:
- Before starting a task: call list_tasks(status="PENDING", blocked=false), pick one, then call claim_task with a note describing your plan.
- While working: call update_task with progress notes if a task takes multiple steps.
- When done: call update_task(status="COMPLETED", commit_hash="<hash>", note="<summary>").
- If you can't finish: call update_task(status="FAILED", note="<why and next steps>").
- To plan new work: call add_task for each step, wire depends_on so tasks execute in the right order.`;

function McpPromptsContent() {
  return (
    <SectionWrap id="mcp-prompts" title="Prompts & agent workflows">
      <P>
        The server exposes four MCP prompts — reusable instructional templates
        any prompt-aware client can invoke. Prompts don't run queries; they
        return step-by-step guidance that primes the agent before it calls the
        tools.
      </P>
      <P>
        In Claude Desktop, open the prompt picker (paperclip icon). In Claude
        Code, use <Code>/mcp</Code> then select the prompt. Any MCP client can
        call <Code>prompts/get</Code> directly.
      </P>

      <H3>Prompt reference</H3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="pb-2 pr-4 font-medium">Name</th>
              <th className="pb-2 pr-4 font-medium">Args</th>
              <th className="pb-2 font-medium">Purpose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr>
              <td className="py-2 pr-4">
                <Code>start-task</Code>
              </td>
              <td className="py-2 pr-4 text-muted-foreground">
                project_id?
              </td>
              <td className="py-2 text-muted-foreground">
                Find and claim the next unblocked PENDING task
              </td>
            </tr>
            <tr>
              <td className="py-2 pr-4">
                <Code>complete-task</Code>
              </td>
              <td className="py-2 pr-4 text-muted-foreground">task_id</td>
              <td className="py-2 text-muted-foreground">
                Close a task as COMPLETED, FAILED, or DROPPED with the right
                note and commit hash
              </td>
            </tr>
            <tr>
              <td className="py-2 pr-4">
                <Code>triage-tasks</Code>
              </td>
              <td className="py-2 pr-4 text-muted-foreground">
                goal, project_id?
              </td>
              <td className="py-2 text-muted-foreground">
                Decompose a goal into dependency-ordered{" "}
                <Code>add_task</Code> calls
              </td>
            </tr>
            <tr>
              <td className="py-2 pr-4">
                <Code>review-progress</Code>
              </td>
              <td className="py-2 pr-4 text-muted-foreground">task_id</td>
              <td className="py-2 text-muted-foreground">
                Read the timeline, spot stalls, decide next steps
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <H3>Sample system prompt</H3>
      <P>
        Drop this into your agent's system instructions to bootstrap effective
        TodoLLM usage without relying on the client supporting prompts:
      </P>
      <Pre>{SYSTEM_PROMPT}</Pre>
      <P>
        This covers the same patterns encoded in the four MCP prompts. Use
        whichever approach fits your client best — they are equivalent.
      </P>
    </SectionWrap>
  );
}

const SECTION_COMPONENTS: Record<string, React.FC> = {
  overview: OverviewContent,
  quickstart: QuickstartContent,
  accounts: AccountsContent,
  organizations: OrganizationsContent,
  projects: ProjectsContent,
  tasks: TasksContent,
  timeline: TimelineContent,
  "api-keys": ApiKeysContent,
  "mcp-overview": McpOverviewContent,
  "mcp-setup": McpSetupContent,
  "mcp-clients": McpClientsContent,
  "mcp-dot-mcp": McpDotMcpContent,
  "mcp-tools": McpToolsContent,
  "mcp-loop": McpLoopContent,
  "mcp-prompts": McpPromptsContent,
};

function FooterCta({ session }: { session: Session | null | undefined }) {
  return (
    <>
      <Separator />
      <p className="pt-4 text-center text-sm">
        {session ? (
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            Back to dashboard
          </Link>
        ) : (
          <Link
            to="/sign-up"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            Sign Up
          </Link>
        )}
      </p>
    </>
  );
}
